import { useState, useEffect } from 'react';
import { FaCog, FaDatabase, FaHdd, FaCheck, FaExclamationTriangle, FaCloud, FaServer } from 'react-icons/fa';
import api, { getServerMode, setServerMode } from '../../api';

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export default function AdminSettings() {
  const [settings, setSettings]     = useState(null);
  const [stats, setStats]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [error, setError]           = useState('');

  const [serverMode, setServerModeState] = useState(getServerMode);

  const handleServerSwitch = (mode) => {
    setServerMode(mode);
    setServerModeState(mode);
    window.location.reload();
  };

  // Local draft of storage choice
  const [storageChoice, setStorageChoice] = useState('disk');
  const [timeoutMins, setTimeoutMins]     = useState(5);
  const [savedTimeout, setSavedTimeout]   = useState(false);
  const [savingTimeout, setSavingTimeout] = useState(false);

  useEffect(() => {
    Promise.all([api.getSettings(), api.getStorageStats()])
      .then(([s, st]) => {
        setSettings(s);
        setStats(st);
        setStorageChoice(s.client_timesheet_storage?.value || 'disk');
        setTimeoutMins(parseInt(s.session_timeout_minutes?.value || '5'));
      })
      .catch(() => setError('Failed to load settings.'))
      .finally(() => setLoading(false));
  }, []);

  const handleSaveTimeout = async () => {
    setSavingTimeout(true);
    setError('');
    try {
      await api.updateSetting('session_timeout_minutes', String(timeoutMins));
      setSavedTimeout(true);
      setTimeout(() => setSavedTimeout(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingTimeout(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await api.updateSetting('client_timesheet_storage', storageChoice);
      setSettings(prev => ({
        ...prev,
        client_timesheet_storage: { value: storageChoice, updated_at: new Date().toISOString() },
      }));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const diskStats  = stats.find(s => s.storage_type === 'disk');
  const dbStats    = stats.find(s => s.storage_type === 'db');
  const totalFiles = stats.reduce((s, r) => s + parseInt(r.file_count), 0);
  const totalBytes = stats.reduce((s, r) => s + parseInt(r.total_bytes), 0);

  return (
    <div className="bg-slate-50 min-h-screen">

      {/* Header */}
      <div className="bg-gradient-to-r from-[#0c1b4d] to-[#1d4ed8] text-white">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FaCog size={20} /> Admin Settings
          </h1>
          <p className="text-blue-200 text-sm mt-1">System-wide configuration</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">

        {loading && <p className="text-sm text-gray-400">Loading settings…</p>}

        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
            <FaExclamationTriangle size={13} /> {error}
          </div>
        )}

        {!loading && (
          <>
            {/* API Server Switch */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="font-bold text-[#0c1b4d] mb-1">API Server Connection</h2>
              <p className="text-sm text-gray-500 mb-5">
                Switch which backend this browser talks to. Both read/write the same database.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => handleServerSwitch('cloud')}
                  className={`text-left p-5 rounded-xl border-2 transition-all ${
                    serverMode === 'cloud' ? 'border-[#0c1b4d] bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${serverMode === 'cloud' ? 'bg-[#0c1b4d]' : 'bg-gray-100'}`}>
                      <FaCloud size={16} className={serverMode === 'cloud' ? 'text-white' : 'text-gray-400'} />
                    </div>
                    {serverMode === 'cloud' && (
                      <span className="text-xs font-semibold text-[#0c1b4d] bg-blue-100 px-2 py-0.5 rounded-full">Active</span>
                    )}
                  </div>
                  <p className="font-semibold text-gray-800 text-sm">Cloudflare</p>
                  <p className="text-xs text-gray-500 mt-1">Cloudflare Worker — no real-time push, updates via polling every few seconds</p>
                  <p className="text-xs text-gray-400 mt-2 font-mono truncate">{process.env.REACT_APP_API_URL || 'https://api.marvyitservices.com/api'}</p>
                </button>

                <button
                  type="button"
                  onClick={() => handleServerSwitch('local')}
                  className={`text-left p-5 rounded-xl border-2 transition-all ${
                    serverMode === 'local' ? 'border-[#0c1b4d] bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${serverMode === 'local' ? 'bg-[#0c1b4d]' : 'bg-gray-100'}`}>
                      <FaServer size={16} className={serverMode === 'local' ? 'text-white' : 'text-gray-400'} />
                    </div>
                    {serverMode === 'local' && (
                      <span className="text-xs font-semibold text-[#0c1b4d] bg-blue-100 px-2 py-0.5 rounded-full">Active</span>
                    )}
                  </div>
                  <p className="font-semibold text-gray-800 text-sm">Oracle Cloud</p>
                  <p className="text-xs text-gray-500 mt-1">Oracle Cloud server — real-time updates, this is the default for employees</p>
                  <p className="text-xs text-gray-400 mt-2 font-mono truncate">{process.env.REACT_APP_LOCAL_API_URL || 'https://api.marvyitservices.com/api'}</p>
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-4">Switching reloads the page. Choice is saved in your browser only.</p>
            </div>

            {/* Storage Setting */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="font-bold text-[#0c1b4d] mb-1">Client Timesheet Storage</h2>
              <p className="text-sm text-gray-500 mb-5">
                Choose where uploaded client timesheets are stored. Applies to all new uploads.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">

                {/* Disk option */}
                <button
                  type="button"
                  onClick={() => setStorageChoice('disk')}
                  className={`text-left p-5 rounded-xl border-2 transition-all ${
                    storageChoice === 'disk'
                      ? 'border-[#0c1b4d] bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                      storageChoice === 'disk' ? 'bg-[#0c1b4d]' : 'bg-gray-100'
                    }`}>
                      <FaHdd size={16} className={storageChoice === 'disk' ? 'text-white' : 'text-gray-400'} />
                    </div>
                    {storageChoice === 'disk' && (
                      <span className="text-xs font-semibold text-[#0c1b4d] bg-blue-100 px-2 py-0.5 rounded-full">Active</span>
                    )}
                  </div>
                  <p className="font-semibold text-gray-800 text-sm">Disk Storage</p>
                  <p className="text-xs text-gray-500 mt-1">Files saved in <code className="bg-gray-100 px-1 rounded">server/uploads/</code> folder on the server</p>
                  <ul className="mt-3 space-y-1">
                    {['Fast downloads', 'Easy folder backup', 'No DB size impact'].map(p => (
                      <li key={p} className="text-xs text-gray-500 flex items-center gap-1.5">
                        <FaCheck size={9} className="text-emerald-500 flex-shrink-0" /> {p}
                      </li>
                    ))}
                  </ul>
                  {diskStats && (
                    <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
                      {diskStats.file_count} file{diskStats.file_count !== '1' ? 's' : ''} · {formatBytes(parseInt(diskStats.total_bytes))}
                    </div>
                  )}
                </button>

                {/* Database option */}
                <button
                  type="button"
                  onClick={() => setStorageChoice('db')}
                  className={`text-left p-5 rounded-xl border-2 transition-all ${
                    storageChoice === 'db'
                      ? 'border-[#0c1b4d] bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                      storageChoice === 'db' ? 'bg-[#0c1b4d]' : 'bg-gray-100'
                    }`}>
                      <FaDatabase size={16} className={storageChoice === 'db' ? 'text-white' : 'text-gray-400'} />
                    </div>
                    {storageChoice === 'db' && (
                      <span className="text-xs font-semibold text-[#0c1b4d] bg-blue-100 px-2 py-0.5 rounded-full">Active</span>
                    )}
                  </div>
                  <p className="font-semibold text-gray-800 text-sm">Database Storage</p>
                  <p className="text-xs text-gray-500 mt-1">Files stored as binary data inside PostgreSQL — backed up with the database</p>
                  <ul className="mt-3 space-y-1">
                    {['Single backup covers everything', 'No missing files on server move', 'ACID-safe with timesheet data'].map(p => (
                      <li key={p} className="text-xs text-gray-500 flex items-center gap-1.5">
                        <FaCheck size={9} className="text-emerald-500 flex-shrink-0" /> {p}
                      </li>
                    ))}
                  </ul>
                  {dbStats && (
                    <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
                      {dbStats.file_count} file{dbStats.file_count !== '1' ? 's' : ''} · {formatBytes(parseInt(dbStats.total_bytes))}
                    </div>
                  )}
                </button>
              </div>

              {/* Current saved vs draft indicator */}
              {settings?.client_timesheet_storage?.value !== storageChoice && (
                <div className="mb-4 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg">
                  <FaExclamationTriangle size={11} />
                  Unsaved — current setting is <strong>{settings?.client_timesheet_storage?.value}</strong>.
                  Existing files are not moved when you switch.
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  onClick={handleSave}
                  disabled={saving || settings?.client_timesheet_storage?.value === storageChoice}
                  className="px-5 py-2.5 bg-[#0c1b4d] text-white text-sm font-semibold rounded-xl hover:bg-blue-900 transition-colors disabled:opacity-40"
                >
                  {saving ? 'Saving…' : 'Save Setting'}
                </button>
                {saved && (
                  <span className="text-sm text-emerald-600 flex items-center gap-1.5 font-medium">
                    <FaCheck size={12} /> Saved
                  </span>
                )}
              </div>

              {settings?.client_timesheet_storage?.updated_at && (
                <p className="text-xs text-gray-400 mt-3">
                  Last changed: {new Date(settings.client_timesheet_storage.updated_at).toLocaleString()}
                </p>
              )}
            </div>

            {/* Session Timeout */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="font-bold text-[#0c1b4d] mb-1">Session Timeout</h2>
              <p className="text-sm text-gray-500 mb-5">
                Automatically log out users after this many minutes of inactivity. A warning appears 1 minute before logout.
              </p>
              <div className="flex items-center gap-4">
                <input
                  type="number"
                  min={1}
                  max={480}
                  value={timeoutMins}
                  onChange={e => setTimeoutMins(Math.min(480, Math.max(1, parseInt(e.target.value) || 1)))}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center font-semibold focus:outline-none focus:ring-2 focus:ring-[#0c1b4d]"
                />
                <span className="text-sm text-gray-500">minutes</span>
                <button
                  onClick={handleSaveTimeout}
                  disabled={savingTimeout}
                  className="px-5 py-2.5 bg-[#0c1b4d] text-white text-sm font-semibold rounded-xl hover:bg-blue-900 transition-colors disabled:opacity-40"
                >
                  {savingTimeout ? 'Saving…' : 'Save'}
                </button>
                {savedTimeout && (
                  <span className="text-sm text-emerald-600 flex items-center gap-1.5 font-medium">
                    <FaCheck size={12} /> Saved
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-3">Range: 1 – 480 minutes</p>
            </div>

            {/* Storage summary */}
            {totalFiles > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h2 className="font-bold text-[#0c1b4d] mb-4 text-sm">Attachment Storage Summary</h2>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: 'Total Files',    value: totalFiles },
                    { label: 'Total Size',     value: formatBytes(totalBytes) },
                    { label: 'Storage Mode',   value: settings?.client_timesheet_storage?.value === 'db' ? 'Database' : 'Disk' },
                  ].map((s, i) => (
                    <div key={i} className="bg-slate-50 rounded-xl p-4">
                      <p className="text-xl font-bold text-[#0c1b4d]">{s.value}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
